import React, { useMemo } from 'react';
import { formatCurrency } from '../../utils/currency.ts';
import { monotoneCubicArea, monotoneCubicPath } from './smoothCurve.ts';

export interface SparkPoint {
  timestamp: string;
  valor: number;
}

interface Props {
  points: SparkPoint[];
  empty?: boolean;
}

const W = 528;
const H = 168;
const PAD_X = 12;
const PAD_T = 16;
const PAD_B = 28;

function formatAxisDate(iso: string, withDay: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (withDay) {
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
  return d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '');
}

/** Line + area for one asset. Flat or single-point series stay visible (dots + faixa). */
const AssetSparkline: React.FC<Props> = ({ points, empty }) => {
  const ordered = useMemo(() => {
    return [...points]
      .filter(p => Number.isFinite(p.valor))
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [points]);

  const geo = useMemo(() => {
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_T - PAD_B;

    if (!ordered.length) {
      return null;
    }

    const vals = ordered.map(p => p.valor);
    let minV = Math.min(...vals);
    let maxV = Math.max(...vals);
    const flat = minV === maxV;
    if (flat) {
      const pad = Math.max(Math.abs(minV) * 0.08, 1);
      minV -= pad;
      maxV += pad;
    } else {
      const pad = (maxV - minV) * 0.12;
      minV -= pad;
      maxV += pad;
    }
    const span = maxV - minV || 1;
    const xAt = (i: number) =>
      PAD_X + (ordered.length === 1 ? innerW / 2 : (i / (ordered.length - 1)) * innerW);
    const yAt = (v: number) => PAD_T + innerH - ((v - minV) / span) * innerH;

    const coords = ordered.map((p, i) => ({
      x: xAt(i),
      y: yAt(p.valor),
    }));
    const baseY = PAD_T + innerH;
    const path = monotoneCubicPath(coords);
    const area = monotoneCubicArea(coords, baseY);

    const first = ordered[0].valor;
    const last = ordered[ordered.length - 1].valor;
    const pct = first > 0 ? ((last - first) / first) * 100 : 0;
    const sameMonth =
      formatAxisDate(ordered[0].timestamp, false) ===
      formatAxisDate(ordered[ordered.length - 1].timestamp, false);

    const guides = [maxV, (maxV + minV) / 2, minV].map(v => yAt(v));

    return {
      path,
      area,
      coords,
      guides,
      baseY,
      firstLabel: formatAxisDate(ordered[0].timestamp, sameMonth || ordered.length === 1),
      lastLabel: formatAxisDate(ordered[ordered.length - 1].timestamp, sameMonth),
      first,
      last,
      pct,
      flat: Math.abs(pct) < 0.05,
      single: ordered.length === 1,
    };
  }, [ordered]);

  const fmt = (v: number) => formatCurrency(v, 'BRL', 'pt-BR');

  if (empty || !geo) {
    return (
      <div className="sparkline-card">
        <div className="muted" style={{ padding: '28px 0', textAlign: 'center', fontSize: 13 }}>
          Sem histórico de preço. Atualize o valor de mercado para criar o 1º ponto.
        </div>
      </div>
    );
  }

  const stroke = geo.pct >= 0 ? '#16a34a' : '#dc2626';

  return (
    <div className="sparkline-card">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Histórico do ativo">
        <defs>
          <linearGradient id="sparkFillDetail" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {geo.guides.map((y, i) => (
          <line
            key={i}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={y}
            y2={y}
            stroke="#e2e8f0"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {geo.area && <path d={geo.area} fill="url(#sparkFillDetail)" />}
        {geo.coords.length > 1 && (
          <path
            d={geo.path}
            fill="none"
            stroke={stroke}
            strokeWidth={2.5}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {geo.coords.map((c, i) => (
          <circle
            key={i}
            cx={c.x}
            cy={c.y}
            r={geo.coords.length > 12 ? 0 : 3.5}
            fill="#fff"
            stroke={stroke}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <text x={PAD_X} y={H - 8} fill="#64748b" fontSize={11} fontFamily="Inter,system-ui,sans-serif">
          {geo.firstLabel}
        </text>
        {!geo.single && (
          <text
            x={W - PAD_X}
            y={H - 8}
            textAnchor="end"
            fill="#64748b"
            fontSize={11}
            fontFamily="Inter,system-ui,sans-serif"
          >
            {geo.lastLabel}
          </text>
        )}
      </svg>
      <div className="sparkline-foot">
        <span>
          {fmt(geo.first)}
          {!geo.single && (
            <>
              {' → '}
              {fmt(geo.last)}
            </>
          )}
        </span>
        <span style={{ color: geo.pct >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
          {geo.single
            ? '1 ponto'
            : geo.flat
              ? 'Preço estável'
              : `${geo.pct >= 0 ? '+' : ''}${geo.pct.toLocaleString('pt-BR', {
                  maximumFractionDigits: 1,
                })}%`}
        </span>
      </div>
    </div>
  );
};

export default AssetSparkline;
